import { StepConfigUpdatable, UpdateStepConfigRequest } from '@/actions/stepConfig';
import { RootState } from '@/reducers/reducers';
import { selectOrg, selectWorkflowId } from '@/selectors/selectors';
import { AgeRatingDeclaration, ageRatingNeedsAttention, AgeRatingQuestionSet } from '@/types/ageRating';
import { RunwayResponseType } from '@/types';
import { FetchableEntityTypeV2 } from '@/entityType';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable, useGetEntityV2 } from '@/utils/hooks/helpers';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { runwayColors } from '../../styles/globalStyles';
import { AgeRatingQuestionnaireModal } from './ageRatingQuestionnaire';

interface AgeRatingSettingsFieldProps {
    appId: string;
    stepConfigId: string;
    declaration?: AgeRatingDeclaration | null;
    ascDeclaration?: AgeRatingDeclaration | null;
    storedQuestionSetVersion?: number | null;
    appDefaultDeclaration?: AgeRatingDeclaration | null;
    questionSet?: AgeRatingQuestionSet | null;
    isEditable: boolean;
}

export const AgeRatingSettingsField: React.FC<AgeRatingSettingsFieldProps> = ({
    appId,
    stepConfigId,
    declaration,
    ascDeclaration,
    storedQuestionSetVersion,
    appDefaultDeclaration,
    questionSet,
    isEditable,
}) => {
    const orgId = useSelector((state: RootState) => selectOrg(state)?.id);
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));

    const [isOpen, setIsOpen] = React.useState(false);

    const openModal = () => {
        setIsOpen(true);
    };

    const [updateStepConfig, isSaving, responseType, saveError] = useCallUpdatable(
        StepConfigUpdatable.UpdateStepConfig,
        {
            workflowId: workflowId ?? '',
            orgId: orgId ?? '',
            stepConfigId,
        },
    );

    const [refetchStepConfig] = useGetEntityV2(FetchableEntityTypeV2.StepConfig, stepConfigId);

    const savePendingRef = React.useRef(false);
    const didHandleUpdateRef = React.useRef(false);
    React.useEffect(() => {
        if (responseType === RunwayResponseType.Success && savePendingRef.current && !didHandleUpdateRef.current) {
            didHandleUpdateRef.current = true;
            savePendingRef.current = false;
            globalMessage.success('Age rating declaration saved');
            setIsOpen(false);
            if (workflowId && orgId) {
                refetchStepConfig({ workflowId, stepConfigId, orgId });
            }
        } else if (saveError && savePendingRef.current) {
            savePendingRef.current = false;
        }
        if (responseType !== RunwayResponseType.Success) {
            didHandleUpdateRef.current = false;
        }
    }, [responseType, saveError, workflowId, orgId, stepConfigId, refetchStepConfig]);

    const usesAppDefault = !declaration && !!appDefaultDeclaration;

    const needsAttention =
        !usesAppDefault && ageRatingNeedsAttention(declaration, storedQuestionSetVersion, questionSet?.version);

    const isPrefilledFromASC = !declaration && !appDefaultDeclaration && !!ascDeclaration;
    const initialValues = declaration ?? appDefaultDeclaration ?? ascDeclaration ?? {};

    const updateAgeRatingDeclaration = (nextDeclaration: AgeRatingDeclaration | null) => {
        const requestData: UpdateStepConfigRequest = {
            stepConfigArchetypeSpecificSettings: {
                ageRatingDeclaration: nextDeclaration,
                ageRatingQuestionSetVersion: questionSet?.version ?? 0,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any,
        };

        updateStepConfig(requestData);
    };

    const onSave = (nextDeclaration: AgeRatingDeclaration) => {
        savePendingRef.current = true;
        updateAgeRatingDeclaration(nextDeclaration);
    };

    // Without a catalog from the backend we can't render the questionnaire.
    if (!questionSet) {
        return null;
    }

    return (
        <>
            <Form.Item
                label={
                    <Space>
                        <Text>Age rating</Text>
                        <Tooltip title="Set the App Store Connect age rating questionnaire answers that Runway applies to new versions.">
                            <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                        </Tooltip>
                    </Space>
                }
            >
                <Space direction="vertical" size={4}>
                    <Badge dot={needsAttention} color={runwayColors.statusYellow}>
                        <Button onClick={openModal} disabled={!isEditable}>
                            {initialValues && Object.keys(initialValues).length > 0 ?
                                'Edit age rating'
                            :   'Set up age rating'}
                        </Button>
                    </Badge>
                    {needsAttention && (
                        <Tooltip
                            title={
                                declaration ?
                                    'Apple has added new age rating questions. Review and re-save to stay compliant.'
                                :   'Age rating has not been configured yet.'
                            }
                        >
                            <Text type="warning">
                                <WarningOutlined /> Needs attention
                            </Text>
                        </Tooltip>
                    )}
                </Space>
            </Form.Item>

            <AgeRatingQuestionnaireModal
                title="Age rating"
                open={isOpen}
                onCancel={() => setIsOpen(false)}
                onSave={onSave}
                questionSet={questionSet}
                initialValues={initialValues}
                isEditable={isEditable}
                saving={isSaving}
                description={
                    isPrefilledFromASC ?
                        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                            Prefilled from your current App Store Connect answers. Review and save to set them for this
                            step.
                        </Text>
                    :   undefined
                }
                footer={
                    <>
                        <Divider />
                        <Text type="secondary" style={{ display: 'block' }}>
                            Saving applies these answers to App Store Connect. App Store Connect carries the age rating
                            forward to new versions, and Runway surfaces unanswered questions at submission.
                        </Text>
                    </>
                }
            />
        </>
    );
};
